import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCheck, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';
import { useNavigate } from 'react-router-dom';

function Notifications() {
  const { user, updateBalance, API } = useContext(AppContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      
      // Update balance to refresh unread count
      await updateBalance();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
      
      // Update balance to refresh unread count
      await updateBalance();
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleVerifyFromNotification = async (notification) => {
    setProcessing(notification.id);
    try {
      await axios.post(`${API}/transactions/${notification.transaction_id}/verify`);
      toast.success('Transaction verified! Tokens released from escrow.');
      
      // Mark notification as read
      await markAsRead(notification.id);
      
      // Update balance
      await updateBalance();
      
      // Refresh notifications
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify transaction');
    } finally {
      setProcessing(null);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'completion_alert':
        return <CheckCheck className="w-5 h-5 text-green-600" />;
      default:
        return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return <div className="text-center py-8">Loading notifications...</div>;
  }

  return (
    <div className="space-y-6" data-testid="notifications">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Notifications</h1>
          <p className="text-gray-600">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={markAllAsRead} variant="outline" data-testid="mark-all-read">
            Mark All as Read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card className="bg-white shadow-lg">
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No notifications yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              className={`shadow-lg transition-colors ${
                notification.read ? 'bg-white' : 'bg-blue-50 border-blue-200'
              }`}
              data-testid={`notification-${notification.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      notification.read ? 'bg-gray-100' : 'bg-blue-100'
                    }`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div>
                      <CardTitle className="text-base">
                        {notification.type === 'completion_alert' ? 'Work Completed!' : 'Notification'}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-1 text-xs mt-1">
                        <Clock className="w-3 h-3" />
                        {new Date(notification.created_at).toLocaleString()}
                      </CardDescription>
                    </div>
                  </div>
                  {!notification.read && (
                    <Badge className="bg-blue-600">New</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{notification.message}</p>
              </CardContent>
              <CardFooter className="flex gap-2">
                {notification.type === 'completion_alert' && !notification.read && (
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleVerifyFromNotification(notification)}
                    disabled={processing === notification.id}
                    data-testid={`verify-from-notification-${notification.id}`}
                  >
                    {processing === notification.id ? 'Processing...' : 'Verify & Complete'}
                  </Button>
                )}
                {!notification.read && (
                  <Button
                    variant="outline"
                    onClick={() => markAsRead(notification.id)}
                    data-testid={`mark-read-${notification.id}`}
                  >
                    Mark as Read
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default Notifications;
